import { DataBinding } from '@se/se.ui.html5';
import { CheckedDataService, ICheckedEntity } from '../models/abstractions';

/**
 * Questa classe gestisce una lista di CheckedDataService.
 *
 * I metodi @getEntities e @getSelectedEntity sono invocati a partire dal primo elemento della lista
 * finchè non viene ritornato un risultato diverso da undefined.
 *
 */
export class CheckedDataServiceList {
    private _checkedDataServices: CheckedDataService[] = [];
    private _currentEntity?: ICheckedEntity;

    constructor(checkedDataServices: CheckedDataService[] | null) {
        if (!!checkedDataServices) this._checkedDataServices = checkedDataServices;
    }

    /**
     * Metodo invocato per ottenere tutte le entity di un certo path.
     *
     * Il metodo viene invocato a partire dal primo elemento della lista
     * finchè non viene ritornato un risultato diverso da undefined.
     * @param binding path del percorso da navigare per recuperare le entity richieste. Il path non deve contenere gli indici.
     *
     * @example 'tbCardioExam.tbCsProc.tbCsValvReport.tbCsValvReportSheet'
     */
    getEntities(binding: DataBinding): ICheckedEntity[] {
        const bindingPath = binding?.toString() ?? '';

        if (bindingPath.startsWith('X.') || bindingPath.includes('.X.') || bindingPath === 'X')
            return !!this._currentEntity ? [this._currentEntity] : [];

        for (let checkedDataService of this._checkedDataServices) {
            const entities: ICheckedEntity[] | null = checkedDataService.getEntities(binding) ?? null;

            if (!!entities) return entities;
        }

        return [];
    }

    /**
     * Metodo invocato per ottenere la entity selezionata di un certo path.
     *
     * Il metodo viene invocato a partire dal primo elemento della lista
     * finchè non viene ritornato un risultato diverso da undefined.
     * @param binding path del percorso da navigare per recuperare le entity richieste. Il path non deve contenere gli indici.
     * @param createMissingEntities se creare l'entity richiesta nel caso non esistesse ancora.
     *
     * @example 'tbCardioExam.tbCsProc.tbCsValvReport.tbCsValvReportSheet'
     */
    getSelectedEntity(binding: DataBinding, createMissingEntities?: boolean): ICheckedEntity | undefined {
        const parameterIndex = binding.full.findIndex((p) => p.value === 'X');
        if (parameterIndex !== -1) {
            const remainingPath = new DataBinding(binding.full.slice(parameterIndex + 1));
            if (this._currentEntity && 'getSelectedEntity' in this._currentEntity) {
                return (this._currentEntity as any).getSelectedEntity(remainingPath);
            } else {
                return this._currentEntity;
            }
        }

        for (let checkedDataService of this._checkedDataServices) {
            const entity: ICheckedEntity | null =
                checkedDataService.getSelectedEntity(binding, createMissingEntities) ?? null;
            if (!!entity) return entity;
        }

        return undefined;
    }

    /**
     * Invocato dal motore per impostare il valore di una proprietà quando il campo formulaTarget del constraint è valorizzato
     * @param binding Il nome della proprietà Path, comprensivo di indici, della entity a partire dalla root
     *
     * @example 'tbCardioExam.0.tbCsProc.0.tbCsValvReport.1.tbCsValvReportSheet.5.comment'
     *
     * @param value Il valore da impostare
     *
     * @param emitEvent Se emettere o meno l'evento di modificato (default: true)
     */
    setValue(binding: DataBinding, value: unknown, emitEvent = true) {
        for (let checkedDataService of this._checkedDataServices) {
            if (!checkedDataService.setValue) continue;

            checkedDataService.setValue(binding, value, { emitEvent, autoUpdated: true });
        }
    }

    /**
     * Invocato dal motore per impostare il valore di una proprietà quando il campo formulaTarget del constraint è valorizzato
     * ed è di tipo guid (o meglio, è collegato ad una data source)
     * @param binding Invocato dal motore per impostare il valore di una proprietà quando il campo formulaTarget del constraint è valorizzato
     * @param guid Il guid da impostare
     * @param idDataSource L'id della dataSource collegata
     * @param emitEvent Se emettere o meno l'evento di modificato (default: true)
     */
    setDatasourceValue(binding: DataBinding, guid: string, idDataSource: string, emitEvent = true) {
        for (let checkedDataService of this._checkedDataServices) {
            if (!checkedDataService.setDatasourceValue) continue;

            checkedDataService.setDatasourceValue(binding, guid, idDataSource, emitEvent);
        }
    }

    setCurrentEntity(currentEntity: ICheckedEntity) {
        this._currentEntity = currentEntity;
    }

    resetCurrentEntity() {
        this._currentEntity = undefined;
    }
}
